/**
 * IntakePreview — a compact read-only sample of a batch's landed raw rows
 * (INTAKE-PRD §2.8.5, slice U-C). Mounted as the footer of a Play intake prompt
 * card so the user SEES the data they're classifying, inline in the walk. Fetches
 * a ≤5-row sample through the `intake.preview` contract (no raw fetch) and renders
 * it with the base Table atoms — never a hand-rolled `<table>`.
 *
 * Presentational: it only shows the sample. Field-level mapping adjust is a
 * separate escape (the drill's Mapping Designer), never inline here.
 */

import * as React from "react";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../../table";
import { useApi } from "../../api/client";
import { intake as intakeContract } from "../../api/contracts/intake";

export interface IntakePreviewProps {
  batchUid: string;
  className?: string;
}

export function IntakePreview({ batchUid, className }: IntakePreviewProps) {
  const { data, isLoading } = useApi(intakeContract.preview, { query: { batchUid } });

  if (isLoading && !data) {
    return <p className={className}>Loading a sample of the data…</p>;
  }
  if (!data || data.columns.length === 0 || data.rows.length === 0) return null;

  return (
    <div className={className}>
      <Table>
        <TableHeader>
          <TableRow>
            {data.columns.map((c) => (
              <TableHead key={c}>{c}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.rows.map((row, i) => (
            <TableRow key={i}>
              {data.columns.map((c) => (
                <TableCell key={c} className="font-mono type-subtext">
                  {row[c] ?? ""}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
